import React from "react";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import {
  BarChart,
  Cell,
  Bar,
  XAxis,
  Tooltip,
} from "recharts";
import IconButton from "@material-ui/core/IconButton";

const CustomTooltip = () => ({
  render() {
    const { active } = this.props;

    if (active) {
      const { payload, label } = this.props;

      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: "rgba(100,100,100,0.8)",
            color: "white",
            padding: "3px",
            borderRadius: "5px",
          }}
        >
          <p className="label">
            {`${label} : ${
              payload
              && payload[0]
              && payload[0].value.toLocaleString(
                "pl-PL",
                { maximumFractionDigits: 0 },
              )
            }`}
          </p>
        </div>
      );
    }

    return null;
  },
});

const posorneg = (type, value) => {
  let color = "black";
  let positive = value >= 0;

  if (type === "cost") {
    positive = !positive;
  }

  if (!positive) {
    color = "#FE8E3C";
  }

  if (type === "text") {
    color = "black";
  }

  return color;
};

/*
 * Nowa struktura:
 *
 * {
 *   groups: [...],
 *   total: {...}
 * }
 *
 * jest zamieniana na dotychczasową tablicę:
 *
 * [
 *   ...groups,
 *   total
 * ]
 *
 * Dla zgodności komponent nadal akceptuje również
 * bezpośrednio tablicę.
 */
const compareOpkId = (a, b) => (
    String(a?.opk_id ?? "").localeCompare(
      String(b?.opk_id ?? ""),
      "pl",
      {
        numeric: true,
        sensitivity: "base",
      },
    )
  );
  
  const sortRowsByOpkId = (rows) => (
    [...rows]
      .sort(compareOpkId)
      .map((row) => ({
        ...row,
  
        children: Array.isArray(row.children)
          ? sortRowsByOpkId(row.children)
          : row.children,
      }))
  );
  
  const normalizeTableData = (data) => {
    /*
     * Obsługa starej struktury będącej bezpośrednio tablicą.
     * Rekord Ogółem zostaje zawsze na końcu.
     */
    if (Array.isArray(data)) {
      const total = data.find(
        (row) => row.opk_id === "Ogółem",
      );
  
      const rows = data.filter(
        (row) => row.opk_id !== "Ogółem",
      );
  
      return total
        ? [...sortRowsByOpkId(rows), total]
        : sortRowsByOpkId(rows);
    }
  
    if (!data || typeof data !== "object") {
      return [];
    }
  
    const groups = Array.isArray(data.groups)
      ? sortRowsByOpkId(data.groups)
      : [];
  
    return data.total
      ? [...groups, data.total]
      : groups;
  };

const CostByStagesTable = (props) => {
  const maximumFractionDigits =
    props.maximumFractionDigits || 0;

  const minimumFractionDigits =
    props.minimumFractionDigits || 0;

  const customMarginRight =
    props.marginRight || "0px";

  const tinyChartWidth =
    props.tinyChartWidth || 150;

  const tableData = React.useMemo(
    () => normalizeTableData(props.data),
    [props.data],
  );

  const CostByStagesMain = (mainProps) => {
    const { dimUnexpanded } = mainProps;

    const [openNodes, setOpenNodes] =
      React.useState([]);

    React.useEffect(() => {
      setOpenNodes([]);
    }, [mainProps.data]);

    const handleOpenNodes = (id) => {
      const temp = [...openNodes];
      const idx = temp.indexOf(id);

      if (idx === -1) {
        if (dimUnexpanded === "true") {
          temp.length = 0;
        }

        temp.push(id);
      } else {
        temp.splice(idx, 1);
      }

      setOpenNodes(temp);
    };

    const formatValue = (value) => {
      if (
        value === null
        || value === undefined
        || value === 0
      ) {
        return "-";
      }

      return value.toLocaleString("pl-PL", {
        maximumFractionDigits,
        minimumFractionDigits,
      });
    };

    function RowKB(rowProps) {
      function Row(childProps) {
        const { row } = childProps;

        return (
          <React.Fragment>
            {row && (
              <TableRow
                key={childProps.rowkey}
                hover
                className="animate__animated animate__fadeIn"
              >
                <TableCell
                  component="th"
                  scope="row"
                  style={{
                    paddingLeft:
                      30 + 15 * childProps.lvl,
                    borderBottom: "0px solid",
                  }}
                >
                  {row.children
                    && row.children.length > 0
                    && (
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        style={{ color: "lightgray" }}
                        onClick={() =>
                          handleOpenNodes(
                            childProps.rowkey,
                          )
                        }
                      >
                        {openNodes.includes(
                          childProps.rowkey,
                        )
                          ? <KeyboardArrowDownIcon />
                          : <KeyboardArrowRightIcon />}
                      </IconButton>
                    )}
                </TableCell>

                {childProps.columns.map((column) => (
                  <TableCell
                    key={column.value}
                    component="th"
                    scope="row"
                    align={column.align}
                  >
                    {column.type === "graph" ? (
                      <div>
                        <svg
                          className="rankBar"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox={
                            `${row.graphMin} 0 200 5`
                          }
                          preserveAspectRatio="none"
                          version="1.1"
                        >
                          <rect
                            x={Math.min(
                              0,
                              row[column.value],
                            )}
                            width={Math.abs(
                              row[column.value],
                            )}
                            height="5"
                            stroke="none"
                            strokeWidth="1"
                            fill={
                              row[column.value] > 0
                                ? "#0B6471"
                                : "orange"
                            }
                          />
                        </svg>
                      </div>
                    ) : column.type === "tinychart" ? (
                      <BarChart
                        style={{
                          padding: "0px",
                          margin: "0px",
                        }}
                        width={tinyChartWidth}
                        height={40}
                        data={row[column.value]}
                        isAnimationActive={false}
                      >
                        <Tooltip
                          content={<CustomTooltip />}
                        />

                        <XAxis
                          dataKey="m"
                          hide
                        />

                        <Bar
                          isAnimationActive={false}
                          dataKey="cost"
                          fill="#0B6471"
                          barSize={
                            tinyChartWidth
                            / (1.5 * 12)
                          }
                        >
                          {row[column.value]
                            && row[column.value].map(
                              (entry, index) => (
                                <Cell
                                  key={index}
                                  cursor="pointer"
                                  fill={
                                    entry.cost < 0
                                      ? "#FE8E3C"
                                      : "#cee0e3"
                                  }
                                />
                              ),
                            )}
                        </Bar>
                      </BarChart>
                    ) : (
                      <Typography
                        variant="body2"
                        style={{
                          paddingLeft: 0,
                          color: posorneg(
                            column.type,
                            row[column.value],
                          ),
                          fontSize: column.fontSize,
                        }}
                      >
                        {formatValue(
                          row[column.value],
                        )}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            )}

            {openNodes.includes(
              childProps.rowkey,
            )
              && row.children
              && row.children.map(
                (child, uid) => (
                  <Row
                    key={
                      `${childProps.rowkey}_${uid}`
                    }
                    rowkey={
                      `${childProps.rowkey}_${uid}`
                    }
                    row={child}
                    lvl={childProps.lvl + 1}
                    columns={childProps.columns}
                  />
                ),
              )}
          </React.Fragment>
        );
      }

      return (
        <React.Fragment>
          <TableRow
            key={rowProps.rowkey}
            hover
            style={{
              backgroundColor:
                openNodes.includes(
                  rowProps.rowkey,
                )
                  ? "#f5f9f9"
                  : undefined,
              borderRadius: "5px",
              outline: "none",
              opacity:
                openNodes.length === 0
                  ? 1
                  : openNodes.includes(
                    rowProps.rowkey,
                  )
                    ? 1
                    : 0.2,
            }}
          >
            <TableCell
              component="th"
              scope="row"
              style={{
                borderBottom: "0px solid",
                borderRadius:
                  "5px 0px 0px 5px",
              }}
            >
              {rowProps.data.children
                && rowProps.data.children.length > 0
                && (
                  <IconButton
                    aria-label="expand row"
                    size="small"
                    onClick={() =>
                      handleOpenNodes(
                        rowProps.rowkey,
                      )
                    }
                  >
                    {openNodes.includes(
                      rowProps.rowkey,
                    )
                      ? <KeyboardArrowDownIcon />
                      : <KeyboardArrowRightIcon />}
                  </IconButton>
                )}
            </TableCell>

            {rowProps.columns.map((column) => (
              <TableCell
                key={column.value}
                align={column.align}
                style={{
                  fontWeight:
                    (
                      openNodes.includes(
                        rowProps.rowkey,
                      )
                      || rowProps.data.opk_id
                        === "Ogółem"
                    )
                      ? 500
                      : undefined,
                  color: posorneg(
                    column.type,
                    rowProps.data[column.value],
                  ),
                }}
              >
                {column.type === "graph" ? (
                  <div>
                    <svg
                      className="rankBar"
                      style={{
                        opacity:
                          openNodes.length === 0
                            ? 1
                            : 0,
                      }}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox={
                        `${rowProps.data.graphMin} 0 200 5`
                      }
                      preserveAspectRatio="none"
                      version="1.1"
                    >
                      <rect
                        x={Math.min(
                          0,
                          rowProps.data[column.value],
                        )}
                        width={Math.abs(
                          rowProps.data[column.value],
                        )}
                        height="5"
                        stroke="none"
                        strokeWidth="1"
                        fill={
                          rowProps.data[column.value] > 0
                            ? "#0B6471"
                            : "orange"
                        }
                      />
                    </svg>
                  </div>
                ) : column.type === "tinychart" ? (
                  <BarChart
                    style={{
                      padding: "0px",
                      margin: "0px",
                    }}
                    width={tinyChartWidth}
                    height={40}
                    data={
                      rowProps.data[column.value]
                    }
                    isAnimationActive={false}
                  >
                    <Tooltip
                      content={<CustomTooltip />}
                    />

                    <XAxis
                      dataKey="m"
                      hide
                    />

                    <Bar
                      isAnimationActive={false}
                      dataKey="cost"
                      fill="#0B6471"
                      barSize={
                        tinyChartWidth / (1.5 * 12)
                      }
                    >
                      {rowProps.data[column.value]
                        && rowProps.data[
                          column.value
                        ].map((entry, index) => (
                          <Cell
                            key={index}
                            cursor="pointer"
                            fill={
                              entry.cost < 0
                                ? "#FE8E3C"
                                : "#cee0e3"
                            }
                          />
                        ))}
                    </Bar>
                  </BarChart>
                ) : column.type === "mixed" ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "3px",
                      border: "1px solid #ececec",
                      backgroundColor: "#f9f9f9",
                      padding: "5px",
                    }}
                  >
                    {formatValue(
                      rowProps.data[column.value],
                    )}

                    <div>
                      <BarChart
                        style={{
                          padding: "0px",
                          margin: "0px",
                        }}
                        width={100}
                        height={30}
                        data={
                          rowProps.data[
                            column.chartValue
                          ]
                        }
                        isAnimationActive={false}
                      >
                        <Tooltip
                          content={
                            <CustomTooltip />
                          }
                        />

                        <XAxis
                          dataKey="m"
                          hide
                        />

                        <Bar
                          isAnimationActive={false}
                          dataKey="cost"
                          fill="#0B6471"
                          barSize={
                            100 / (1.5 * 12)
                          }
                        >
                          {rowProps.data[
                            column.chartValue
                          ]
                            && rowProps.data[
                              column.chartValue
                            ].map(
                              (entry, index) => (
                                <Cell
                                  key={index}
                                  cursor="pointer"
                                  fill={
                                    entry.cost < 0
                                      ? "#FE8E3C"
                                      : "#cee0e3"
                                  }
                                />
                              ),
                            )}
                        </Bar>
                      </BarChart>
                    </div>
                  </div>
                ) : (
                  formatValue(
                    rowProps.data[column.value],
                  )
                )}
              </TableCell>
            ))}
          </TableRow>

          {openNodes.includes(
            rowProps.rowkey,
          )
            && rowProps.data.children
            && rowProps.data.children.map(
              (row, uid) => (
                <Row
                  key={`${rowProps.rowkey}_${uid}`}
                  rowkey={
                    `${rowProps.rowkey}_${uid}`
                  }
                  row={row}
                  columns={rowProps.columns}
                  lvl={0}
                />
              ),
            )}
        </React.Fragment>
      );
    }

    return (
      <Paper
        style={{
          minWidth: "410px",
          width: "100%",
          padding: "5px",
          marginBottom: "10px",
          marginRight: customMarginRight,
        }}
      >
        <Typography
          style={{
            color: "#0b6471",
            fontSize: "16px",
            fontWeight: "100",
            borderBottom: "0px solid",
            paddingLeft: "10px",
            paddingTop: "5px",
          }}
        >
          {mainProps.title || "ETAPY ALOKACJI"}
        </Typography>

        <TableContainer>
          <Table
            size="small"
            aria-label="simple table"
          >
            <TableHead
              style={{ border: "none" }}
            >
              <TableRow>
                <TableCell
                  style={{
                    borderBottom: "0px solid",
                    width: "2px",
                  }}
                />

                {mainProps.columns.map(
                  (column) => (
                    <TableCell
                      key={column.value}
                      style={{
                        color: "#0b6471",
                        fontSize: "16px",
                        fontWeight: "100",
                        borderBottom:
                          column.underline,
                      }}
                      align={column.align}
                    >
                      {column.name}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {mainProps.data.map(
                (account, uid) => (
                  <RowKB
                    key={uid}
                    data={account}
                    rowkey={uid}
                    columns={mainProps.columns}
                    dimUnexpanded={
                      dimUnexpanded
                    }
                  />
                ),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <CostByStagesMain
      data={tableData}
      title={props.title}
      columns={props.columns}
      dimUnexpanded={props.dimUnexpanded}
    />
  );
};

export default CostByStagesTable;